import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { Rocket, TrackChanges, MenuBook, Launch } from "@mui/icons-material";
import { Context as AppContext } from "../../context/AppContext";
import { MainLayout } from "../../layouts/main-layout/MainLayout";
import { enums } from "../../utils/enums";
import { useNavigate } from "react-router-dom";
import './mentorHomepage.css'
import animationData from '../../assets/student.json';
import Lottie from "lottie-react";
import InternshipCard from '../../components/InternshipCard';

const InternHomepage = () => {
    const theme = useTheme();
    const { state: { currentUser, mentors, internships }, getMentors, getInternships } = useContext(AppContext);
    const [internship, setInternship] = useState(null)

    const navigate = useNavigate()
    useEffect(() => {
        if (currentUser) {
            getMentors()
            const internshipUid = currentUser?.internshipUid
            if (internshipUid) {
                getInternships({uid: currentUser.uid, type: currentUser.role})
            }
        }
    }, [currentUser])

    useEffect(() => {
        if (internships?.length > 0) {
            setInternship(internships[0])
        }
    }, [internships])

    if ([enums.ADMIN, enums.STUDENT, enums.TEACHER].includes(currentUser?.role)) {
        navigate('/')
    }
    const capitalizeFirstWord = (str) => {
        if (!str) return '';
        const firstWord = str.split(' ')[0];
        return firstWord.charAt(0).toUpperCase() + firstWord.slice(1).toLowerCase();
    };

    return (
        <MainLayout>
            <Box sx={{
                minHeight: '100vh',
                bgcolor: '#fafbfc',
                fontFamily: 'Poppins, system-ui, -apple-system, sans-serif'
            }}>
                <Box sx={{
                    maxWidth: '1200px',
                    mx: 'auto',
                    px: { xs: 2, md: 3 },
                    py: { xs: 2, md: 3 }
                }}>
                    {/* Welcome Header */}
                    <Box sx={{ mb: 3 }}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontSize: { xs: '1.5rem', md: '2rem' },
                                fontWeight: 600,
                                color: '#1a1a1a',
                                mb: 1,
                                letterSpacing: '-0.02em'
                            }}
                        >
                            Welcome,
                            <span className={'rainbow-text'}>    {capitalizeFirstWord(currentUser?.name)} </span>
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: '#64748b',
                                fontSize: '1rem',
                                maxWidth: '600px'
                            }}
                        >
                            Your journey to professional excellence starts here.
                        </Typography>
                    </Box>

                    {!currentUser?.internshipUid ? (
                        <Box sx={{
                            bgcolor: 'white',
                            borderRadius: '20px',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
                            overflow: 'hidden',
                            transition: 'box-shadow 0.2s ease-in-out',
                            '&:hover': {
                                boxShadow: '0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                            }
                        }}>
                            <Box sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
                                <Box sx={{ mb: 4 }}>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontSize: { xs: '1.125rem', md: '1.25rem' },
                                            fontWeight: 600,
                                            color: '#1e293b',
                                            mb: 2
                                        }}
                                    >
                                        Ready to Start Your Mentorship Journey?
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: '#64748b',
                                            fontSize: '1rem',
                                            maxWidth: '500px',
                                            mx: 'auto',
                                            lineHeight: 1.6
                                        }}
                                    >
                                        You don't have a mentor yet. Apply to work with industry professionals and accelerate your career growth.
                                    </Typography>
                                </Box>

                                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                                    <Lottie
                                        animationData={animationData}
                                        loop={true}
                                        style={{ height: '180px', width: '180px' }}
                                    />
                                </Box>

                                {!currentUser?.hasApplied ? (
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={() => navigate('/mentors')}
                                        disabled={currentUser?.hasApplied}
                                        sx={{
                                            bgcolor: theme.palette.primary.main,
                                            color: 'white',
                                            fontSize: '1rem',
                                            fontWeight: 600,
                                            px: 4,
                                            py: 1.5,
                                            borderRadius: '12px',
                                            textTransform: 'none',
                                            minWidth: '220px',
                                            boxShadow: `0 4px 14px 0 ${theme.palette.primary.main}40`,
                                            '&:hover': {
                                                bgcolor: theme.palette.primary.dark || '#4c2a6b',
                                                boxShadow: `0 6px 20px 0 ${theme.palette.primary.main}59`,
                                                transform: 'translateY(-1px)'
                                            },
                                            '&:focus': {
                                                outline: `2px solid ${theme.palette.primary.main}`,
                                                outlineOffset: '2px'
                                            },
                                            transition: 'all 0.2s ease-in-out'
                                        }}
                                    >
                                        Apply for a Mentorship
                                    </Button>
                                ) : (
                                    <Box sx={{
                                        bgcolor: theme.palette.secondary.main,
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '12px',
                                        p: 3,
                                        mt: 3,
                                        maxWidth: '400px',
                                        mx: 'auto'
                                    }}>
                                        <Typography variant="body1" sx={{ mb: 1, color: '#64748b' }}>
                                            Awaiting response from
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontSize: '1.125rem',
                                                fontWeight: 600,
                                                color: theme.palette.primary.main
                                            }}
                                        >
                                            {mentors?.find(mentor => mentor?.uid === currentUser?.mentorApplications[0])?.name}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#64748b', mt: 1, fontStyle: 'italic' }}>
                                            You will be notified when your application is reviewed
                                        </Typography>
                                    </Box>
                                )}

                                {/* Feature Cards */}
                                <Box sx={{
                                    mt: 4,
                                    pt: 3,
                                    borderTop: '1px solid #f1f5f9',
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                                    gap: 3
                                }}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Box sx={{
                                            width: 48,
                                            height: 48,
                                            bgcolor: '#f0f9ff',
                                            borderRadius: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mx: 'auto',
                                            mb: 2
                                        }}>
                                            <TrackChanges sx={{ fontSize: '1.5rem', color: '#3b82f6' }} />
                                        </Box>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 600,
                                                color: '#1e293b',
                                                mb: 0.5
                                            }}
                                        >
                                            Set Goals
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: '#64748b' }}
                                        >
                                            Define your objectives
                                        </Typography>
                                    </Box>

                                    <Box sx={{ textAlign: 'center' }}>
                                        <Box sx={{
                                            width: 48,
                                            height: 48,
                                            bgcolor: '#f0fdf4',
                                            borderRadius: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mx: 'auto',
                                            mb: 2
                                        }}>
                                            <MenuBook sx={{ fontSize: '1.5rem', color: '#10b981' }} />
                                        </Box>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 600,
                                                color: '#1e293b',
                                                mb: 0.5
                                            }}
                                        >
                                            Learn & Grow
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: '#64748b' }}
                                        >
                                            Develop new skills
                                        </Typography>
                                    </Box>

                                    <Box sx={{ textAlign: 'center' }}>
                                        <Box sx={{
                                            width: 48,
                                            height: 48,
                                            bgcolor: '#fefce8',
                                            borderRadius: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mx: 'auto',
                                            mb: 2
                                        }}>
                                            <Launch sx={{ fontSize: '1.5rem', color: '#f59e0b' }} />
                                        </Box>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 600,
                                                color: '#1e293b',
                                                mb: 0.5
                                            }}
                                        >
                                            Launch Career
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: '#64748b' }}
                                        >
                                            Start your journey
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{
                            bgcolor: 'white',
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
                            p: { xs: 2, md: 3 },
                            transition: 'box-shadow 0.2s ease-in-out',
                            '&:hover': {
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                            }
                        }}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 3,
                                flexWrap: 'wrap',
                                gap: 2
                            }}>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontSize: { xs: '1.25rem', md: '1.5rem' },
                                        fontWeight: 600,
                                        color: '#1e293b'
                                    }}
                                >
                                    My Mentorship
                                </Typography>
                                <Box sx={{
                                    px: 2,
                                    py: 0.5,
                                    bgcolor: theme.palette.secondary.main,
                                    color: theme.palette.primary.main,
                                    borderRadius: '20px',
                                    fontSize: '0.875rem',
                                    fontWeight: 500
                                }}>
                                    Active Program
                                </Box>
                            </Box>
                            <InternshipCard internship={internship} intern={currentUser} index={0} />
                        </Box>
                    )}
                </Box>
            </Box>
        </MainLayout>
    );
};

export default InternHomepage;